/**
 * API métier établissement : établissements, lits, banques-sang, produits, carte, notifications.
 * Base: /api/etablissement (préfixé par VITE_API_BASE_URL + '/api' dans client).
 */

import { apiFetch } from "./client";

const BASE = "/etablissement";

// --- Types minimaux (alignés avec les DTO backend)

/** Prix lit (Annexe A.3 HELPEUR). */
export type PrixLit = {
  prixParJour?: number;
  prixParHeure?: number;
  reduction?: { type?: string; valeur?: number; condition?: string };
};

/**
 * Lit — Annexe A.3 + §4.3 docs/api/HELPEUR_USAGE_etablissement.md.
 * `total` / `disponible` : champs possibles hors annexe (rétrocompat).
 */
export type Lit = {
  id?: string;
  etablissementId?: string;
  numero?: string;
  chambre?: string;
  etage?: string;
  type?: string;
  service?: string;
  statut?: string;
  patientNip?: string | null;
  dateDebut?: string | null;
  dateFin?: string | null;
  raison?: string | null;
  prix?: PrixLit;
  equipements?: unknown[];
  dateCreation?: string;
  dateModification?: string;
  createur?: string;
  reservation?: unknown;
  total?: number;
  disponible?: number;
  categorie?: string;
  [key: string]: unknown;
};

/** Body POST : le validateur exige `prix` = { prixParJour, prixParHeure? }. */
export type CreateLitPayload = {
  etablissementId: string;
  numero: string;
  type: string;
  service: string;
  prix: { prixParJour: number; prixParHeure?: number };
  createur?: string;
  chambre?: string;
  etage?: string;
  statut?: string;
  equipements?: unknown[];
};

export type LitReferentielItem = {
  id?: string;
  code?: string;
  libelle?: string;
  nom?: string;
  description?: string;
};

export type BanqueSang = {
  id: string;
  etablissementId?: string;

  // Informations de la poche
  groupeSanguin: 'A' | 'B' | 'AB' | 'O';
  rhesus: 'positif' | 'negatif';
  volume: number; // en ml

  // Dates importantes
  dateCollecte: string; // backend renvoie souvent une date ISO sérialisée
  datePeremption: string;

  // Informations du donneur
  lot: string;

  // Stockage
  temperature: number;
  localisation: string;

  // Statut (ex: disponible / reservee / utilisee / expiree / rejetee)
  statut?: string;

  // Métadonnées
  createur?: string;
  [key: string]: unknown;
};

export type StockSangItem = {
  groupeSanguin?: string;
  quantite?: number;
  sacs?: Array<{ sac?: string; rhesus?: string; volume?: string; collecte?: string; peremption?: string }>;
  [key: string]: unknown;
};

export type CreateBanqueSangPayload = {
  groupeSanguin: 'A' | 'B' | 'AB' | 'O';
  rhesus: 'positif' | 'negatif';
  volume: number; // ml
  dateCollecte: string;
  datePeremption: string;
  lot: string;
  temperature?: number;
  localisation?: string;
  statut?: string;
  createur: string; // email / NIP selon backend
};

export type UpdateBanqueSangPayload = Partial<
  Pick<CreateBanqueSangPayload, 'groupeSanguin' | 'rhesus' | 'volume' | 'temperature' | 'localisation' | 'statut'>
> & {
  dateCollecte?: string;
  datePeremption?: string;
  lot?: string;
};

export type DemandeSangPayload = {
  // backend attend un groupe combiné : `A+`, `AB-`, ...
  groupeSanguin: 'A+' | 'A-' | 'B+' | 'B-' | 'AB+' | 'AB-' | 'O+' | 'O-';
  quantite: number; // ml
  urgence: 'faible' | 'moyenne' | 'haute' | 'critique';
  motif: string;
};

// --- Mapping UI <-> backend (ex: `AB+` -> `groupeSanguin=AB`, `rhesus=positif`)
export function groupLabelToBackendGroupAndRhesus(label: string):
  | { groupeSanguin: 'A' | 'B' | 'AB' | 'O'; rhesus: 'positif' | 'negatif' }
  | null {
  const s = String(label || '').trim();
  if (!s) return null;
  const last = s.endsWith('+') ? '+' : s.endsWith('-') ? '-' : null;
  if (!last) return null;

  const rhesus = last === '+' ? 'positif' : 'negatif';
  const groupe = s.slice(0, -1).toUpperCase();
  if (groupe !== 'A' && groupe !== 'B' && groupe !== 'AB' && groupe !== 'O') return null;
  return { groupeSanguin: groupe as 'A' | 'B' | 'AB' | 'O', rhesus };
}

export function backendGroupAndRhesusToGroupLabel(groupeSanguin: BanqueSang['groupeSanguin'], rhesus: BanqueSang['rhesus']): string {
  const suffix = rhesus === 'positif' ? '+' : '-';
  return `${groupeSanguin}${suffix}`;
}

/** Objet prix renvoyé par l'API (FCFA, %). */
export type ProduitPrix = {
  prixUnitaire: number;
  prixVente: number;
  marge: number;
  tva?: number;
};

export type ProduitStatut =
  | "disponible"
  | "alerte"
  | "rupture_stock"
  | "expire"
  | "hors_service";

export type ProduitMedicament = {
  id?: string;
  etablissementId?: string;
  name?: string;
  nom?: string;
  classe?: string;
  category?: string;
  qty?: number;
  quantite?: number;
  /** Prix : objet { prixUnitaire, prixVente, marge, tva } (backend). */
  prix?: ProduitPrix;
  price?: string;
  date?: string;
  dateMaj?: string;
  /** Backend peut envoyer dateModification / dateCreation. */
  dateModification?: string;
  dateCreation?: string;
  /** Champs de disponibilité — retournés par le backend. */
  stockMinimum?: number;
  seuilAlerte?: number;
  quantiteReservee?: number;
  datePeremption?: string;
  statut?: ProduitStatut;
  [key: string]: unknown;
};

export type NotificationItem = {
  id: string;
  title?: string;
  titre?: string;
  message?: string;
  time?: string;
  date?: string;
  read?: boolean;
  lu?: boolean;
  [key: string]: unknown;
};

export type MapGpsParams = {
  latitude: number;
  longitude: number;
  radius?: number;
};

export type MapSangParGroupe = {
  disponible: number;
  reservee: number;
  utilisee: number;
  expiree: number;
  rejetee: number;
};

export type MapEtablissementItem = {
  id: string;
  nom?: string;
  name?: string;
  type?: string;
  coordonnees?: { latitude: number; longitude: number };
  lat?: number;
  latitude?: number;
  lng?: number;
  longitude?: number;
  litsCount?: number;
  sangCount?: number;
  produitsCount?: number;
  sangParGroupe?: Record<string, MapSangParGroupe>;
  tauxOccupationLits?: number;
  bedsAvailable?: number;
  bloodSummary?: Record<string, number>;
  productsCount?: number;
  city?: string;
  commune?: string;
  tags?: string[];
  [key: string]: unknown;
};

export type MapDataResponse = {
  center?: { latitude: number; longitude: number };
  radius?: number;
  etablissements?: MapEtablissementItem[];
  resources?: {
    lits?: unknown[];
    sang?: unknown[];
    produits?: unknown[];
  };
  statistics?: {
    totalEtablissements?: number;
    totalLits?: number;
    totalSang?: number;
    totalProduits?: number;
  };
};

export type MapCartographieData = MapDataResponse;

export type MapAvailabilityResponse = {
  lits?: { total?: number; disponibles?: number; occupes?: number; parType?: Record<string, number> };
  sang?: { total?: number; parGroupe?: Record<string, number>; volumeTotal?: number };
  produits?: { total?: number; parType?: Record<string, number>; parCategorie?: Record<string, number> };
};

export type MapStatisticsResponse = {
  region?: { coordinates?: { latitude: number; longitude: number }; radius?: number };
  etablissements?: { total?: number; parType?: Record<string, number>; parVille?: Record<string, number> };
  ressources?: {
    lits?: { total?: number; disponibles?: number; tauxOccupation?: number };
    sang?: { totalPoches?: number; volumeTotal?: number; parGroupe?: Record<string, number> };
    produits?: { total?: number; parType?: Record<string, number>; stockFaible?: number };
  };
  generatedAt?: string;
};

export type MapRoutePayload = {
  startLatitude: number;
  startLongitude: number;
  establishmentId: string;
};

export type MapRouteResponse = {
  origin?: { latitude: number; longitude: number };
  destination?: { latitude: number; longitude: number };
  distance?: number;
  duration?: number;
  steps?: unknown[];
  etablissement?: { id?: string; nom?: string; type?: string; adresse?: string };
};

export type MapSearchParams = MapGpsParams & {
  type: 'etablissement' | 'lits' | 'sang' | 'banque_sang' | 'produits' | 'pharmacie';
};

// --- Établissements

export async function updateEstablishmentLocation(
  establishmentId: string,
  latitude: number,
  longitude: number
): Promise<{ ok: true; data?: unknown } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`${BASE}/etablissements/${establishmentId}`, {
    method: "PUT",
    body: JSON.stringify({ coordonnees: { latitude, longitude } }),
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: out.data };
}

// --- Lits (contrat : docs/api/HELPEUR_USAGE_etablissement.md §4.3, Annexe A.3 ; le backend peut ajouter des champs)

/** MongoDB / certains gateways renvoient `_id` sans `id` : sans ça, DELETE/PUT visent un mauvais segment d’URL. */
export function normalizeLitFromApi(raw: Lit): Lit {
  if (raw.id != null && String(raw.id) !== "") return raw;
  const oid = (raw as { _id?: unknown })._id;
  if (oid == null || oid === "") return raw;
  return { ...raw, id: String(oid) };
}

function litPathId(id: string): string {
  return encodeURIComponent(id);
}

/**
 * Extrait le tableau de lits depuis le corps JSON renvoyé par apiFetch (racine = body parsé).
 * Gère : tableau nu ; `{ data: Lit[] }` ; `{ data: { data, lits, items } }` (même tableau sous les trois clés) ;
 * `{ lits }` / `{ items }` à la racine.
 */
function extractLitsListFromApiRoot(root: unknown): Lit[] {
  if (root == null) return [];
  if (Array.isArray(root)) return root as Lit[];
  if (typeof root !== "object") return [];
  const r = root as Record<string, unknown>;
  const inner = r.data;
  if (inner != null && typeof inner === "object" && !Array.isArray(inner)) {
    const bag = inner as Record<string, unknown>;
    const fromBag = bag.data ?? bag.lits ?? bag.items;
    if (Array.isArray(fromBag)) return fromBag as Lit[];
  }
  if (Array.isArray(inner)) return inner as Lit[];
  const flat = r.data ?? r.lits ?? r.items;
  if (Array.isArray(flat)) return flat as Lit[];
  return [];
}

export async function getLits(etablissementId: string): Promise<
  { ok: true; data: Lit[] } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(
    `${BASE}/lits/etablissement/${encodeURIComponent(etablissementId)}`
  );
  if (!out.ok) return { ok: false, message: out.message };
  const data = extractLitsListFromApiRoot(out.data);
  return { ok: true, data: data.map(normalizeLitFromApi) };
}

export async function createLit(
  body: CreateLitPayload | Record<string, unknown>
): Promise<{ ok: true; data?: Lit } | { ok: false; message: string }> {
  const out = await apiFetch<Lit>(`${BASE}/lits`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: out.data };
}

export async function updateLit(id: string, body: Partial<Lit>): Promise<
  { ok: true; data?: Lit } | { ok: false; message: string }
> {
  const out = await apiFetch<Lit>(`${BASE}/lits/${litPathId(id)}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: out.data ? normalizeLitFromApi(out.data) : out.data };
}

export async function deleteLit(id: string): Promise<
  { ok: true } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${BASE}/lits/${litPathId(id)}`, { method: "DELETE" });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true };
}

function normalizeReferentielList(raw: unknown): LitReferentielItem[] {
  if (Array.isArray(raw)) return raw as LitReferentielItem[];
  if (raw && typeof raw === "object" && Array.isArray((raw as { data?: unknown }).data)) {
    return (raw as { data: LitReferentielItem[] }).data;
  }
  return [];
}

export async function getLitsTypesList(): Promise<
  { ok: true; data: LitReferentielItem[] } | { ok: false; message: string }
> {
  const out = await apiFetch<LitReferentielItem[] | { data?: LitReferentielItem[] }>(
    `${BASE}/lits/types/list`
  );
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: normalizeReferentielList(out.data) };
}

export async function getLitsStatutsList(): Promise<
  { ok: true; data: LitReferentielItem[] } | { ok: false; message: string }
> {
  const out = await apiFetch<LitReferentielItem[] | { data?: LitReferentielItem[] }>(
    `${BASE}/lits/statuts/list`
  );
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: normalizeReferentielList(out.data) };
}

export async function getLitsDisponibles(params: {
  etablissementId: string;
  type?: string;
  service?: string;
}): Promise<{ ok: true; data: Lit[] } | { ok: false; message: string }> {
  const q = new URLSearchParams({ etablissementId: params.etablissementId });
  if (params.type) q.set("type", params.type);
  if (params.service) q.set("service", params.service);
  const out = await apiFetch<unknown>(`${BASE}/lits/disponibles?${q.toString()}`);
  if (!out.ok) return { ok: false, message: out.message };
  const data = extractLitsListFromApiRoot(out.data);
  return { ok: true, data: data.map(normalizeLitFromApi) };
}

export async function reserveLit(
  id: string,
  body: { patientId: string; dateDebut?: string; dateFin?: string; motif: string }
): Promise<{ ok: true; data?: unknown } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`${BASE}/lits/${litPathId(id)}/reserver`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: out.data };
}

export async function libererLit(
  id: string,
  body: { motif: string }
): Promise<{ ok: true; data?: unknown } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`${BASE}/lits/${litPathId(id)}/liberer`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: out.data };
}

export async function maintenanceLit(
  id: string,
  body: { motif: string; dureeEstimee: number }
): Promise<{ ok: true; data?: unknown } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`${BASE}/lits/${litPathId(id)}/maintenance`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: out.data };
}

// --- Banques de sang

export async function getBanquesSang(): Promise<
  { ok: true; data: BanqueSang[] } | { ok: false; message: string }
> {
  const out = await apiFetch<BanqueSang[] | { data?: BanqueSang[] }>(`${BASE}/banques-sang`);
  if (!out.ok) return { ok: false, message: out.message };
  const data = Array.isArray(out.data) ? out.data : (out.data as { data?: BanqueSang[] })?.data ?? [];
  return { ok: true, data };
}

export async function getStocks(banqueId: string): Promise<
  { ok: true; data: BanqueSang[] } | { ok: false; message: string }
> {
  const out = await apiFetch<BanqueSang[] | { data?: BanqueSang[] }>(
    `${BASE}/banques-sang/${banqueId}/stocks`
  );
  if (!out.ok) return { ok: false, message: out.message };
  const data = Array.isArray(out.data) ? out.data : (out.data as { data?: BanqueSang[] })?.data ?? [];
  return { ok: true, data };
}

export async function updateStocks(
  banqueId: string,
  stocks: Record<string, number>
): Promise<{ ok: true } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`${BASE}/banques-sang/${banqueId}/stocks`, {
    method: "PUT",
    // backend attendu : `{ stocks: Record<string, number> }`
    body: JSON.stringify({ stocks }),
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true };
}

export async function createBanqueSang(
  payload: CreateBanqueSangPayload
): Promise<{ ok: true; data?: BanqueSang } | { ok: false; message: string }> {
  const out = await apiFetch<BanqueSang>(`${BASE}/banques-sang`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: out.data };
}

export async function updateBanqueSang(
  pocheId: string,
  payload: UpdateBanqueSangPayload
): Promise<{ ok: true; data?: BanqueSang } | { ok: false; message: string }> {
  const out = await apiFetch<BanqueSang>(`${BASE}/banques-sang/${encodeURIComponent(pocheId)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: out.data };
}

export async function deleteBanqueSang(
  pocheId: string
): Promise<{ ok: true } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`${BASE}/banques-sang/${encodeURIComponent(pocheId)}`, {
    method: 'DELETE',
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true };
}

export async function demandSang(
  payload: DemandeSangPayload
): Promise<{ ok: true; data?: unknown } | { ok: false; message: string }> {
  const out = await apiFetch<unknown>(`${BASE}/banques-sang/demande`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: out.data };
}

export async function getGroupesSanguinsList(): Promise<
  { ok: true; data: string[] } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${BASE}/banques-sang/groupes-sanguins/list`);
  if (!out.ok) return { ok: false, message: out.message };
  const data = Array.isArray(out.data) ? out.data : (out.data as { data?: string[] })?.data ?? [];
  return { ok: true, data };
}

// --- Produits médicaments

export type ProduitTypeController = "medicament" | "materiel" | "consommable" | "equipement";

export type ProduitPrixAchatVente = {
  achat: number;
  vente: number;
};

export async function getProduitsMedicaments(etablissementId: string): Promise<
  { ok: true; data: ProduitMedicament[] } | { ok: false; message: string }
> {
  const out = await apiFetch<ProduitMedicament[] | { data?: ProduitMedicament[] }>(
    `${BASE}/produits-medicaments/etablissement/${etablissementId}`
  );
  if (!out.ok) return { ok: false, message: out.message };
  const data = Array.isArray(out.data) ? out.data : (out.data as { data?: ProduitMedicament[] })?.data ?? [];
  return { ok: true, data };
}

export async function getProduitsTypesList(): Promise<
  { ok: true; data: Array<{ code?: string; nom?: string; description?: string }> } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${BASE}/produits-medicaments/types/list`);
  if (!out.ok) return { ok: false, message: out.message };
  const raw = (out.data as { data?: unknown })?.data ?? out.data;
  const data = Array.isArray(raw) ? (raw as Array<{ code?: string; nom?: string; description?: string }>) : [];
  return { ok: true, data };
}

export async function getProduitsCategoriesList(): Promise<
  { ok: true; data: Array<{ code?: string; nom?: string; description?: string }> } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${BASE}/produits-medicaments/categories/list`);
  if (!out.ok) return { ok: false, message: out.message };
  const raw = (out.data as { data?: unknown })?.data ?? out.data;
  const data = Array.isArray(raw) ? (raw as Array<{ code?: string; nom?: string; description?: string }>) : [];
  return { ok: true, data };
}

export async function getProduitsUnitesMesureList(): Promise<
  { ok: true; data: Array<{ code?: string; nom?: string; description?: string }> } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${BASE}/produits-medicaments/unites-mesure/list`);
  if (!out.ok) return { ok: false, message: out.message };
  const raw = (out.data as { data?: unknown })?.data ?? out.data;
  const data = Array.isArray(raw) ? (raw as Array<{ code?: string; nom?: string; description?: string }>) : [];
  return { ok: true, data };
}

export async function createProduit(body: Partial<ProduitMedicament>): Promise<
  { ok: true; data?: ProduitMedicament } | { ok: false; message: string }
> {
  const out = await apiFetch<ProduitMedicament>(`${BASE}/produits-medicaments`, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: out.data };
}

export async function updateProduit(id: string, body: Partial<ProduitMedicament>): Promise<
  { ok: true; data?: ProduitMedicament } | { ok: false; message: string }
> {
  const out = await apiFetch<ProduitMedicament>(`${BASE}/produits-medicaments/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: out.data };
}

export async function deleteProduit(id: string): Promise<
  { ok: true } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${BASE}/produits-medicaments/${id}`, { method: "DELETE" });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true };
}

// --- Map / Cartographie

const MAP = `${BASE}/map-cartographie`;

function buildMapGpsQuery(params?: MapGpsParams): string {
  if (!params) return "";
  const q = new URLSearchParams({ latitude: String(params.latitude), longitude: String(params.longitude) });
  if (params.radius != null) q.set("radius", String(params.radius));
  return "?" + q.toString();
}

function extractListFromApiRoot<T>(root: unknown): T[] {
  if (root == null) return [];
  if (Array.isArray(root)) return root as T[];
  if (typeof root !== "object") return [];
  const r = root as Record<string, unknown>;
  const inner = r.data;
  if (inner != null && typeof inner === "object" && !Array.isArray(inner)) {
    const bag = inner as Record<string, unknown>;
    const fromBag = bag.data ?? bag.items;
    if (Array.isArray(fromBag)) return fromBag as T[];
  }
  if (Array.isArray(inner)) return inner as T[];
  const flat = r.data ?? r.items;
  if (Array.isArray(flat)) return flat as T[];
  return [];
}

function extractMapData(root: unknown): MapDataResponse {
  if (root == null) return { etablissements: [] };
  if (typeof root !== "object") return { etablissements: [] };
  const r = root as Record<string, unknown>;
  // L'API enveloppe dans { success, data: { ... } }
  const inner = r.data;
  if (inner != null && typeof inner === "object" && !Array.isArray(inner)) {
    return inner as MapDataResponse;
  }
  return root as MapDataResponse;
}

export async function getMapCartographieData(params?: MapGpsParams): Promise<
  { ok: true; data: MapCartographieData } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${MAP}/data${buildMapGpsQuery(params)}`);
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractMapData(out.data) };
}

export async function getMapAvailability(params: MapGpsParams): Promise<
  { ok: true; data: MapAvailabilityResponse } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${MAP}/availability${buildMapGpsQuery(params)}`);
  if (!out.ok) return { ok: false, message: out.message };
  const d = out.data as Record<string, unknown>;
  const data = (d?.data ?? out.data) as MapAvailabilityResponse;
  return { ok: true, data };
}

export async function getNearbyEtablissements(params: MapGpsParams): Promise<
  { ok: true; data: MapEtablissementItem[] } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${MAP}/nearby/etablissements${buildMapGpsQuery(params)}`);
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractListFromApiRoot<MapEtablissementItem>(out.data) };
}

export async function getNearbyLitsDisponibles(params: MapGpsParams & { type?: string }): Promise<
  { ok: true; data: Lit[] } | { ok: false; message: string }
> {
  const q = new URLSearchParams({ latitude: String(params.latitude), longitude: String(params.longitude) });
  if (params.radius != null) q.set("radius", String(params.radius));
  if (params.type) q.set("type", params.type);
  const out = await apiFetch<unknown>(`${MAP}/nearby/lits-disponibles?${q.toString()}`);
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractListFromApiRoot<Lit>(out.data).map(normalizeLitFromApi) };
}

export async function getNearbyBanquesSang(params: MapGpsParams): Promise<
  { ok: true; data: BanqueSang[] } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${MAP}/nearby/banques-sang${buildMapGpsQuery(params)}`);
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractListFromApiRoot<BanqueSang>(out.data) };
}

export async function getNearbyPharmacies(params: MapGpsParams & { medicament?: string }): Promise<
  { ok: true; data: ProduitMedicament[] } | { ok: false; message: string }
> {
  const q = new URLSearchParams({ latitude: String(params.latitude), longitude: String(params.longitude) });
  if (params.radius != null) q.set("radius", String(params.radius));
  if (params.medicament) q.set("medicament", params.medicament);
  const out = await apiFetch<unknown>(`${MAP}/nearby/pharmacies?${q.toString()}`);
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractListFromApiRoot<ProduitMedicament>(out.data) };
}

export type NearbyProduitItem = {
  id: string;
  nom: string;
  quantite: number;
  unite: string;
  statut: 'disponible' | 'rupture_stock';
  prix: { prixVente: number };
};

export type NearbyProduitEtablissement = {
  id: string;
  nom: string;
  type?: string;
  adresse?: string;
  coordonnees: {
    latitude: number;
    longitude: number;
    altitude?: number;
    precision?: number;
    adresse?: string;
  } | null;
};

export type NearbyProduitGroup = {
  etablissementId: string;
  etablissement: NearbyProduitEtablissement;
  produits: NearbyProduitItem[];
  distance: number | null;
};

export async function getNearbyProduits(
  params: MapGpsParams & { q?: string }
): Promise<{ ok: true; data: NearbyProduitGroup[] } | { ok: false; message: string }> {
  const qs = new URLSearchParams({ latitude: String(params.latitude), longitude: String(params.longitude) });
  if (params.radius != null) qs.set("radius", String(params.radius));
  if (params.q) qs.set("q", params.q);
  const out = await apiFetch<unknown>(`${MAP}/nearby/produits?${qs.toString()}`);
  if (!out.ok) return { ok: false, message: out.message };
  const root = out.data as Record<string, unknown>;
  const list = Array.isArray(root?.data) ? root.data : Array.isArray(out.data) ? out.data : [];
  return { ok: true, data: list as NearbyProduitGroup[] };
}

export async function getMapStatistics(params: MapGpsParams): Promise<
  { ok: true; data: MapStatisticsResponse } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${MAP}/statistics${buildMapGpsQuery(params)}`);
  if (!out.ok) return { ok: false, message: out.message };
  const d = out.data as Record<string, unknown>;
  const data = (d?.data ?? out.data) as MapStatisticsResponse;
  return { ok: true, data };
}

export async function getMapCoverageZones(params: MapGpsParams): Promise<
  { ok: true; data: MapStatisticsResponse } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${MAP}/coverage-zones${buildMapGpsQuery(params)}`);
  if (!out.ok) return { ok: false, message: out.message };
  const d = out.data as Record<string, unknown>;
  const data = (d?.data ?? out.data) as MapStatisticsResponse;
  return { ok: true, data };
}

export async function getMapAlerts(params: MapGpsParams): Promise<
  { ok: true; data: unknown[] } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${MAP}/alerts${buildMapGpsQuery(params)}`);
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractListFromApiRoot<unknown>(out.data) };
}

export async function postMapRoute(payload: MapRoutePayload): Promise<
  { ok: true; data: MapRouteResponse } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${MAP}/route`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  if (!out.ok) return { ok: false, message: out.message };
  const d = out.data as Record<string, unknown>;
  const data = (d?.data ?? out.data) as MapRouteResponse;
  return { ok: true, data };
}

export async function searchMapResources(params: MapSearchParams): Promise<
  { ok: true; data: unknown[] } | { ok: false; message: string }
> {
  const q = new URLSearchParams({
    type: params.type,
    latitude: String(params.latitude),
    longitude: String(params.longitude),
  });
  if (params.radius != null) q.set("radius", String(params.radius));
  const out = await apiFetch<unknown>(`${MAP}/search?${q.toString()}`);
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractListFromApiRoot<unknown>(out.data) };
}

export async function getMapResourceTypes(): Promise<
  { ok: true; data: unknown[] } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${MAP}/resource-types`);
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractListFromApiRoot<unknown>(out.data) };
}

export async function getMapTransportModes(): Promise<
  { ok: true; data: unknown[] } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${MAP}/transport-modes`);
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true, data: extractListFromApiRoot<unknown>(out.data) };
}

// --- Personnel (liaisons professionnel ↔ établissement, lecture seule côté établissement)

/** Auto-déclarative, non vérifiée par l'établissement — ne pas afficher comme "personnel officiel". */
export type PersonnelSummary = {
  nip: string;
  nom?: string;
  prenom?: string;
  specialite?: string;
  statut?: string;
  [key: string]: unknown;
};

export type PersonnelLiaison = {
  id: string;
  typeLiaison?: string;
  statut?: string;
  poste?: string | null;
  dateDebut?: string | null;
  dateFin?: string | null;
  commentaire?: string | null;
  [key: string]: unknown;
};

export type PersonnelItem = { professionnel: PersonnelSummary; liaison: PersonnelLiaison };

export type PersonnelPagination = { page: number; limit: number; total: number; totalPages: number };

export async function getPersonnel(
  etablissementId: string,
  page = 1,
  limit = 20
): Promise<
  { ok: true; data: PersonnelItem[]; pagination: PersonnelPagination } | { ok: false; message: string }
> {
  const q = new URLSearchParams({ page: String(page), limit: String(limit) });
  const out = await apiFetch<unknown>(
    `${BASE}/etablissements/${encodeURIComponent(etablissementId)}/professionnels?${q.toString()}`
  );
  if (!out.ok) return { ok: false, message: out.message };
  const root = out.data as { data?: unknown; pagination?: PersonnelPagination } | PersonnelItem[];
  const list = Array.isArray(root) ? root : Array.isArray(root?.data) ? (root.data as PersonnelItem[]) : [];
  const pagination = Array.isArray(root)
    ? { page, limit, total: list.length, totalPages: 1 }
    : (root?.pagination ?? { page, limit, total: list.length, totalPages: 1 });
  return { ok: true, data: list, pagination };
}

// --- Demandes d'entrée (liaisons en_attente à approuver/refuser)

export type DemandeEntreeItem = { professionnel: PersonnelSummary; liaison: PersonnelLiaison };

export async function getDemandesEntree(
  etablissementId: string,
  page = 1,
  limit = 20
): Promise<
  { ok: true; data: DemandeEntreeItem[]; pagination: PersonnelPagination } | { ok: false; message: string }
> {
  const q = new URLSearchParams({ page: String(page), limit: String(limit) });
  const out = await apiFetch<unknown>(
    `${BASE}/etablissements/${encodeURIComponent(etablissementId)}/demandes?${q.toString()}`
  );
  if (!out.ok) return { ok: false, message: out.message };
  const root = out.data as { data?: unknown; pagination?: PersonnelPagination } | DemandeEntreeItem[];
  const list = Array.isArray(root) ? root : Array.isArray(root?.data) ? (root.data as DemandeEntreeItem[]) : [];
  const pagination = Array.isArray(root)
    ? { page, limit, total: list.length, totalPages: 1 }
    : (root?.pagination ?? { page, limit, total: list.length, totalPages: 1 });
  return { ok: true, data: list, pagination };
}

// --- Patients de l'établissement (au moins une consultation dans cet établissement)

/** Même shape que `ProfessionnelPatientSummary` côté professionnel. */
export type EtablissementPatientSummary = {
  nip: string;
  nom: string;
  prenom: string;
  email?: string;
  nombreConsultations: number;
  premiereConsultation: string;
  derniereConsultation: string;
};

export async function getEtablissementPatients(
  etablissementId: string,
  page = 1,
  limit = 20
): Promise<
  { ok: true; data: EtablissementPatientSummary[]; pagination: PersonnelPagination } | { ok: false; message: string }
> {
  const offset = (page - 1) * limit;
  const q = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const out = await apiFetch<unknown>(
    `${BASE}/etablissements/${encodeURIComponent(etablissementId)}/patients?${q.toString()}`
  );
  if (!out.ok) return { ok: false, message: out.message };
  const root = out.data as { data?: unknown; pagination?: PersonnelPagination } | EtablissementPatientSummary[];
  const list = Array.isArray(root) ? root : Array.isArray(root?.data) ? (root.data as EtablissementPatientSummary[]) : [];
  const pagination = Array.isArray(root)
    ? { page, limit, total: list.length, totalPages: 1 }
    : (root?.pagination ?? { page, limit, total: list.length, totalPages: 1 });
  return { ok: true, data: list, pagination };
}

async function postDemandeAction(
  etablissementId: string,
  liaisonId: string,
  action: "approve" | "reject"
): Promise<{ ok: true; data?: PersonnelLiaison } | { ok: false; message: string; status?: number }> {
  const out = await apiFetch<unknown>(
    `${BASE}/etablissements/${encodeURIComponent(etablissementId)}/demandes/${encodeURIComponent(liaisonId)}/${action}`,
    { method: "POST" }
  );
  if (!out.ok) return { ok: false, message: out.message, status: out.status };
  const raw = out.data as { data?: PersonnelLiaison } | PersonnelLiaison;
  const data = (raw as { data?: PersonnelLiaison })?.data ?? (raw as PersonnelLiaison);
  return { ok: true, data };
}

export async function approveDemandeEntree(etablissementId: string, liaisonId: string) {
  return postDemandeAction(etablissementId, liaisonId, "approve");
}

export async function rejectDemandeEntree(etablissementId: string, liaisonId: string) {
  return postDemandeAction(etablissementId, liaisonId, "reject");
}

/**
 * Retire un professionnel actif du personnel de l'établissement (l'établissement met fin à la
 * liaison de son côté, symétrique de `endEtablissementLiaison` côté professionnel dans
 * `api/professional.ts`). `commentaire` (motif du retrait) est obligatoire côté backend — min. 5
 * caractères, relayé au professionnel par notification (`retrait_etablissement`).
 */
export async function removePersonnel(
  etablissementId: string,
  liaisonId: string,
  commentaire: string
): Promise<{ ok: true; data?: PersonnelLiaison } | { ok: false; message: string; status?: number; errorCode?: string }> {
  const out = await apiFetch<unknown>(
    `${BASE}/etablissements/${encodeURIComponent(etablissementId)}/professionnels/${encodeURIComponent(liaisonId)}`,
    { method: "DELETE", body: JSON.stringify({ commentaire }) }
  );
  if (!out.ok) return { ok: false, message: out.message, status: out.status, errorCode: out.errorCode };
  const raw = out.data as { data?: PersonnelLiaison } | PersonnelLiaison;
  const data = (raw as { data?: PersonnelLiaison })?.data ?? (raw as PersonnelLiaison);
  return { ok: true, data };
}

// --- Notifications

export type GetNotificationsParams = {
  statut?: "non_lue" | "lue" | "toutes";
  limit?: number;
  offset?: number;
};

export type GetNotificationsResponse = {
  data: NotificationItem[];
  total?: number;
  nonLues?: number;
};

export async function getNotifications(params?: GetNotificationsParams): Promise<
  { ok: true; data: NotificationItem[]; total?: number; nonLues?: number } | { ok: false; message: string }
> {
  const q = new URLSearchParams();
  if (params?.statut) q.set("statut", params.statut);
  if (params?.limit != null) q.set("limit", String(params.limit));
  if (params?.offset != null) q.set("offset", String(params.offset));
  const query = q.toString();
  const path = query ? `${BASE}/notifications?${query}` : `${BASE}/notifications`;
  const out = await apiFetch<NotificationItem[] | GetNotificationsResponse>(path);
  if (!out.ok) return { ok: false, message: out.message };
  const body = out.data as GetNotificationsResponse;
  const data = Array.isArray(body) ? body : body?.data ?? [];
  const total = Array.isArray(body) ? undefined : body?.total;
  const nonLues = Array.isArray(body) ? undefined : body?.nonLues;
  return { ok: true, data, total, nonLues };
}

export async function markNotificationRead(id: string): Promise<
  { ok: true } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${BASE}/notifications/${id}/mark-read`, {
    method: "PUT",
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true };
}

export async function markAllNotificationsRead(): Promise<
  { ok: true } | { ok: false; message: string }
> {
  const out = await apiFetch<unknown>(`${BASE}/notifications/mark-all-read`, {
    method: "PUT",
  });
  if (!out.ok) return { ok: false, message: out.message };
  return { ok: true };
}
